import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext";

// Map of language codes to approximate BCP-47 language tags for SpeechSynthesis
const VOICE_LANG_MAP: Record<string, string[]> = {
  en: ["en-IN", "en-GB", "en-US"],
  hi: ["hi-IN", "hi"],
  or: ["or-IN", "or", "hi-IN", "en-IN"], // Fallback to Hindi/English if Odia missing
  bn: ["bn-IN", "bn", "hi-IN", "en-IN"],
  te: ["te-IN", "te", "en-IN"],
  ta: ["ta-IN", "ta", "en-IN"],
  kn: ["kn-IN", "kn", "en-IN"],
  ml: ["ml-IN", "ml", "en-IN"],
  mr: ["mr-IN", "mr", "hi-IN", "en-IN"],
  gu: ["gu-IN", "gu", "hi-IN", "en-IN"],
  pa: ["pa-IN", "pa", "hi-IN", "en-IN"],
};

export function useSpeech() {
  const { language } = useLanguage();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [supported, setSupported] = useState(true);

  // Load voices
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setSupported(false);
      return;
    }

    const loadVoices = () => {
      setVoices(window.speechSynthesis.getVoices());
    };

    loadVoices();
    // Chrome fires this event when voices are loaded asynchronously
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const stop = useCallback(() => {
    if (supported) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, [supported]);

  const speak = useCallback((text: string) => {
    if (!supported) return;

    // Stop current speech before starting new one
    stop();

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Find appropriate voice based on selected language
    const preferredLangTags = VOICE_LANG_MAP[language] || ["en-US"];
    let selectedVoice = null;

    for (const tag of preferredLangTags) {
      const match = voices.find(v => v.lang.toLowerCase().includes(tag.toLowerCase()));
      if (match) {
        selectedVoice = match;
        break;
      }
    }

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    // Set fallback language tag for the utterance if voice is missing
    utterance.lang = preferredLangTags[0];
    utterance.rate = 0.9; // Slightly slower for better comprehension of operational data

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  }, [language, voices, supported, stop]);

  // ─── Speech Recognition (JARVIS Listening) ─────────────────────────────────
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");

  const listen = useCallback((onResult: (text: string) => void) => {
    if (typeof window === "undefined") return;
    
    // @ts-ignore - Vendor prefixes
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn("Speech Recognition API not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = VOICE_LANG_MAP[language]?.[0] || 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setTranscript("");
    };

    recognition.onresult = (event: any) => {
      const resultText = event.results[0][0].transcript;
      setTranscript(resultText);
      onResult(resultText);
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  }, [language]);

  return { speak, stop, isSpeaking, supported, listen, isListening, transcript };
}
